/**
 * ZombieNewsSystem.js - Zombie outbreak news ticker for Passenger Zero aircraft
 *
 * When Passenger Zero completes its first flight, a zombie outbreak begins.
 * News items appear on a schedule, escalating from mystery illness to full apocalypse.
 * The outbreak is permanent and affects the news ticker for the rest of the game.
 */

import { debug } from '../../lib/debug.js';

// Default news sequence if not provided
export const DEFAULT_ZOMBIE_NEWS_SEQUENCE = [
  { day: 0, text: "3 hospitalized with mystery illness in {destination}" },
  { day: 3, text: "CDC dispatches team to investigate 'Patient Zero' flight" },
  { day: 7, text: "Outbreak spreads to 12 states" },
  { day: 14, text: "Europe closes borders amid health crisis" },
  { day: 21, text: "Military deployed to quarantine zones" },
  { day: 30, text: "Reports of 'unusual behavior' in infected patients" },
  { day: 45, text: "Society beginning to collapse in Southeast Asia" },
  { day: 60, text: "The WHO has stopped updating case counts" },
  { day: 90, text: "The dead walk. Flights continue as scheduled." },
  { day: 120, text: "New normal: Zombie-proof terminals now standard" },
  { day: 180, text: "Airlines report record profits despite apocalypse" },
];

/**
 * Initialize zombie outbreak in game state
 * Called when Passenger Zero completes its first flight
 *
 * @param {Object} setWorldState - setState function from WorldStateContext
 * @param {string} destination - City where Patient Zero landed
 */
export function startZombieOutbreak(setWorldState, destination) {
  setWorldState((prev) => {
    if (prev.zombieOutbreakStarted) {
      // Already started - don't restart
      return prev;
    }

    const newsSequence = DEFAULT_ZOMBIE_NEWS_SEQUENCE.map((item) => ({
      ...item,
      text: item.text.replace("{destination}", destination),
      shown: item.day === 0, // Day 0 news shows immediately
    }));

    // Get the day 0 news text
    const day0News = newsSequence.find(item => item.day === 0);
    const immediateNews = day0News ? {
      severity: 'zombie',
      text: day0News.text,
      headline: day0News.text,
      message: day0News.text,
    } : null;

    debug("[ZombieNews] Outbreak started at", destination);
    debug("[ZombieNews] BREAKING:", day0News?.text);

    return {
      ...prev,
      zombieOutbreakStarted: true,
      zombieOutbreakDay: prev.dayNumber || 1,
      zombieNewsSequence: newsSequence,
      // Push day 0 news immediately to newsFeed
      newsFeed: immediateNews
        ? [immediateNews, ...(prev.newsFeed || [])]
        : prev.newsFeed,
    };
  });
}

/**
 * Check for new zombie news to display
 * Called on each day advance
 *
 * @param {Object} worldState - Current world state
 * @param {Object} setWorldState - setState function
 * @param {Function} addNewsItem - Function to add item to news ticker
 * @returns {string|null} - News text if new item, null otherwise
 */
export function updateZombieNews(worldState, setWorldState, addNewsItem) {
  if (!worldState?.zombieOutbreakStarted) {
    return null;
  }

  const currentDay = worldState.dayNumber || 1;
  const outbreakStartDay = worldState.zombieOutbreakDay || 1;
  const daysSinceOutbreak = currentDay - outbreakStartDay;
  const newsSequence = worldState.zombieNewsSequence || [];

  let newNewsText = null;

  // Check each news item
  const updatedSequence = newsSequence.map((item) => {
    if (item.day <= daysSinceOutbreak && !item.shown) {
      // This news should be shown
      newNewsText = item.text;
      if (addNewsItem) {
        addNewsItem({
          id: `zombie-${item.day}`,
          text: item.text,
          type: "zombie",
          priority: "high",
          timestamp: new Date().toISOString(),
        });
      }
      debug("[ZombieNews] Day", daysSinceOutbreak, ":", item.text);
      return { ...item, shown: true };
    }
    return item;
  });

  // Update state if any news was shown
  if (newNewsText) {
    setWorldState((prev) => ({
      ...prev,
      zombieNewsSequence: updatedSequence,
    }));
  }

  return newNewsText;
}

/**
 * Get current zombie outbreak status
 *
 * @param {Object} worldState - Current world state
 * @returns {Object} - Status object
 */
export function getZombieStatus(worldState) {
  if (!worldState?.zombieOutbreakStarted) {
    return {
      active: false,
      daysSinceOutbreak: 0,
      phase: "none",
      newsShown: 0,
      newsTotal: 0,
    };
  }

  const currentDay = worldState.dayNumber || 1;
  const outbreakStartDay = worldState.zombieOutbreakDay || 1;
  const daysSinceOutbreak = currentDay - outbreakStartDay;
  const newsSequence = worldState.zombieNewsSequence || [];
  const newsShown = newsSequence.filter((n) => n.shown).length;

  // Determine phase based on days
  let phase = "outbreak";
  if (daysSinceOutbreak >= 90) phase = "apocalypse";
  else if (daysSinceOutbreak >= 45) phase = "collapse";
  else if (daysSinceOutbreak >= 21) phase = "military";
  else if (daysSinceOutbreak >= 7) phase = "pandemic";
  else if (daysSinceOutbreak >= 3) phase = "investigation";

  return {
    active: true,
    daysSinceOutbreak,
    phase,
    newsShown,
    newsTotal: newsSequence.length,
  };
}

/**
 * Check if we're in zombie apocalypse (day 90+)
 * Can be used to modify game behavior
 *
 * @param {Object} worldState - Current world state
 * @returns {boolean}
 */
export function isZombieApocalypse(worldState) {
  if (!worldState?.zombieOutbreakStarted) return false;
  const currentDay = worldState.dayNumber || 1;
  const outbreakStartDay = worldState.zombieOutbreakDay || 1;
  return currentDay - outbreakStartDay >= 90;
}

/**
 * Get zombie-themed flavor text for various game elements
 * Returns normal text if no outbreak
 *
 * @param {Object} worldState - Current world state
 * @param {string} context - Context for the text (e.g., "boarding", "delay", "arrival")
 * @returns {string|null} - Zombie-themed text or null
 */
export function getZombieFlavorText(worldState, context) {
  if (!worldState?.zombieOutbreakStarted) return null;

  const status = getZombieStatus(worldState);

  const flavorTexts = {
    boarding: {
      outbreak: "Passengers board nervously, eyeing each other.",
      investigation: "CDC agents scan passengers at the gate.",
      pandemic: "Only essential travel permitted.",
      military: "Armed guards supervise boarding.",
      collapse: "Passengers fight for remaining seats.",
      apocalypse: "The living board alongside the... mostly living.",
    },
    delay: {
      outbreak: "Delay due to passenger health screening.",
      investigation: "Delay: Quarantine checkpoint.",
      pandemic: "Delay: Runway blocked by military convoy.",
      military: "Delay: Airspace restricted for evacuation flights.",
      collapse: "Delay: Fuel convoy attacked en route.",
      apocalypse: "Delay: Clearing runway of the undead.",
    },
    arrival: {
      outbreak: "Passengers reminded to report symptoms.",
      investigation: "All passengers subject to health screening.",
      pandemic: "Mandatory 14-day quarantine upon arrival.",
      military: "Military escort to quarantine zone.",
      collapse: "Passengers advised to proceed to nearest safe zone.",
      apocalypse: "Welcome to what remains. Flights continue as scheduled.",
    },
  };

  return flavorTexts[context]?.[status.phase] || null;
}

export default {
  DEFAULT_ZOMBIE_NEWS_SEQUENCE,
  startZombieOutbreak,
  updateZombieNews,
  getZombieStatus,
  isZombieApocalypse,
  getZombieFlavorText,
};
